// ############################################################
// Copyright (C) 2021 by Gamify Software Inc - All Rights Reserved
// You may NOT distribute or modify this code without the expressed
// written consent of Gamify Software Inc.
// #############################################################
//
// getMyInfo
//

import gql from 'graphql-tag';
import { API, Storage } from 'aws-amplify';
import { useCurrentUserStore } from '../../store/zustand/useCurrentUserStore';

// Global promise cache to prevent multiple concurrent requests
let getMyInfoPromise = null;
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1000; // Minimum 1 second between requests

export async function getMyInfo() {
  // If there's already a request in progress, return the same promise
  if (getMyInfoPromise) {
    return getMyInfoPromise;
  }

  // Rate limiting: prevent requests too close together
  const now = Date.now();
  if (now - lastRequestTime < MIN_REQUEST_INTERVAL) {
    await new Promise((resolve) =>
      setTimeout(resolve, MIN_REQUEST_INTERVAL - (now - lastRequestTime))
    );
  }

  // Create new promise and cache it
  getMyInfoPromise = (async () => {
    let isRateLimitError = false;

    try {
      lastRequestTime = Date.now();
      //
      //  Get the user and config objects
      //
      const myQuery = gql`
        query MyQuery {
          getMyInfo(request: "{}")
        }
      `;

      const meData = await API.graphql({ query: myQuery });
      const configObject = JSON.parse(meData.data.getMyInfo);
      if (Object.keys(configObject).length === 0) {
        return false;
      }
      global.me = configObject.me;
      global.appSettings = configObject.appSettings;
      global.refreshOnNotificationHandler = [];
      global.updateNotificationHandler = [];
      global.notifications = 0;
      if (global.me === undefined) {
        return false;
      }
      if (global?.me?.imageName && global.me.imageName.trim() !== '') {
        try {
          global.me.profilePicture = await Storage.get(global.me.imageName);
        } catch (storageError) {
          console.error('Error loading profile picture:', storageError);
          global.me.profilePicture = '/images/default-avatar.png';
        }
      } else {
        global.me.profilePicture = '/images/default-avatar.png';
      }

      const { getCurrentUserID, getCurrentUserTeamsAndMembers } =
        useCurrentUserStore.getState();

      getCurrentUserID();
      getCurrentUserTeamsAndMembers().catch((err) => {
        console.error('getCurrentUserTeamsAndMembers error: ', err);
      });

      return true;
    } catch (err) {
      console.error("Can't getMyInfo: ", err);

      // Handle specific rate limiting errors
      if (err.message && err.message.includes('TooManyRequestsException')) {
        console.error(
          'getMyInfo: Rate limit exceeded - too many requests to lambda function'
        );
        isRateLimitError = true;
      }

      return false;
    } finally {
      // Clear the promise cache after completion (success or failure)
      // Use setTimeout to allow all pending calls to resolve first
      if (isRateLimitError) {
        // Wait longer before allowing next request for rate limit errors
        setTimeout(() => {
          getMyInfoPromise = null;
        }, 5000); // 5 second delay for rate limit errors
      } else {
        setTimeout(() => {
          getMyInfoPromise = null;
        }, 100);
      }
    }
  })();

  return getMyInfoPromise;
}
